import type { OptionResponse } from "@models/option";
import { activeOptionValues } from "../../_helpers/activeOptionValues";

// Option id -> the id of the value picked in it. One value per option (AC-11).
export type VariantSelection = Readonly<Record<number, number>>;

// What the picker offers: active options with only their active values. Both
// are soft-deleted (AC-10). GET /options leaves out a deleted option, but the
// picker does not rely on that.
export const offeredOptions = (options: OptionResponse[]): OptionResponse[] =>
    options
        .filter((option) => option.isActive)
        .map((option) => ({
            ...option,
            values: activeOptionValues(option.values),
        }));

// Picks `valueId` in its option, replacing that option's earlier pick. Picking
// the value that is already picked un-picks it.
export const togglePick = (
    selection: VariantSelection,
    optionId: number,
    valueId: number,
): VariantSelection => {
    if (selection[optionId] === valueId) {
        const next = { ...selection };
        delete next[optionId];
        return next;
    }
    return { ...selection, [optionId]: valueId };
};

// The picks that are still on offer. A pick whose option or value has since
// been deleted is dropped rather than handed back to the caller.
export const picksOnOffer = (
    selection: VariantSelection,
    offered: OptionResponse[],
): VariantSelection => {
    const kept: Record<number, number> = {};
    for (const option of offered) {
        const valueId = selection[option.id];
        if (option.values.some((value) => value.id === valueId)) {
            kept[option.id] = valueId;
        }
    }
    return kept;
};

// Whether two names or values are the same to an admin: surrounding spaces
// and case are ignored. The API has no unique rule on either, so the picker
// is what keeps "Red" and "red" from becoming two pills.
const sameText = (a: string, b: string): boolean =>
    a.trim().toLowerCase() === b.trim().toLowerCase();

// Adds a typed value to the new option's values: trimmed, and not when blank
// or already there in any case (the same array comes back then).
export const stageValue = (staged: string[], typed: string): string[] => {
    const value = typed.trim();
    if (!value || staged.some((s) => sameText(s, value))) return staged;
    return [...staged, value];
};

// The existing option name a typed new name repeats, if any.
export const optionNamed = (
    names: string[],
    typed: string,
): string | undefined =>
    typed.trim() ? names.find((name) => sameText(name, typed)) : undefined;
